import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/admin/adminlayout';
import { PRODUCTION_URL } from '../productionLink/productionLink';
import './guest-reservation-card.css';

const GuestReservationCard = () => {
    const { bookingId } = useParams();
    const navigate = useNavigate();
    const [booking, setBooking] = useState(null);
    const [payments, setPayments] = useState([]);
    const [guestBills, setGuestBills] = useState([]);
    const [refunds, setRefunds] = useState([]);
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchGuestCard = useCallback(async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const response = await fetch(`${PRODUCTION_URL}/api/admin/guest-card/${bookingId}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) throw new Error('Failed to fetch guest card');

            const data = await response.json();
            if (data.success) {
                setBooking(data.data.booking);
                setPayments(data.data.payments);
                setGuestBills(data.data.guestBills);
                setRefunds(data.data.refunds);
                setSummary(data.data.summary);
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [bookingId]);

    useEffect(() => {
        if (bookingId) {
            fetchGuestCard();
        }
    }, [bookingId, fetchGuestCard]);

    const formatDate = (date) => {
        return new Date(date).toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 2
        }).format(amount || 0);
    };

    const getStatusBadge = (status) => {
        const statusColors = {
            'confirmed': 'status-confirmed',
            'checked_in': 'status-checkedin',
            'checked_out': 'status-checkedout',
            'cancelled': 'status-cancelled',
            'pending': 'status-pending'
        };
        return statusColors[status] || 'status-default';
    };

    const getPaymentStatusBadge = (status) => {
        const colors = {
            'paid': 'payment-paid',
            'partial': 'payment-partial',
            'pending': 'payment-pending'
        };
        return colors[status] || 'payment-pending';
    };

    if (loading) {
        return (
            <div className="guest-card-container">
                <div className="loading-spinner">
                    <div className="spinner"></div>
                    <p>Loading guest details...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="guest-card-container">
                <div className="error-message">
                    <h3>Error</h3>
                    <p>{error}</p>
                    <button onClick={() => navigate(-1)} className="btn-back">Go Back</button>
                </div>
            </div>
        );
    }

    if (!booking) {
        return (
            <div className="guest-card-container">
                <div className="not-found">
                    <h3>Booking Not Found</h3>
                    <button onClick={() => navigate(-1)} className="btn-back">Go Back</button>
                </div>
            </div>
        );
    }

    return (
        <AdminLayout>
            <div className="guest-card-container">
                <div className="guest-card-header">
                    <div className="header-left">
                        <button onClick={() => navigate(-1)} className="btn-back">
                            ← Back
                        </button>
                        <h1>Guest Reservation Card</h1>
                    </div>
                    <div className="header-right">
                        <span className={`status-badge ${getStatusBadge(booking.status)}`}>
                            {booking.status?.replace('_', ' ').toUpperCase()}
                        </span>
                    </div>
                </div>

                <div className="guest-card-content">
                    {/* Guest Information */}
                    <div className="card-section guest-info">
                        <h2>Guest Information</h2>
                        <div className="info-grid">
                            <div className="info-item">
                                <label>Guest Name</label>
                                <span>{booking.guest_name}</span>
                            </div>
                            <div className="info-item">
                                <label>Phone</label>
                                <span>{booking.guest_phone || 'N/A'}</span>
                            </div>
                            <div className="info-item">
                                <label>Email</label>
                                <span>{booking.guest_email || 'N/A'}</span>
                            </div>
                            <div className="info-item">
                                <label>Nationality</label>
                                <span>{booking.guest_nationality}</span>
                            </div>
                            <div className="info-item">
                                <label>ID Proof</label>
                                <span>{booking.guest_id_proof || 'N/A'}</span>
                            </div>
                            <div className="info-item">
                                <label>No. of Guests</label>
                                <span>{booking.number_of_guests}</span>
                            </div>
                        </div>
                    </div>

                    {/* Room & Stay Details */}
                    <div className="card-section stay-details">
                        <h2>Stay Details</h2>
                        <div className="info-grid">
                            <div className="info-item">
                                <label>Room Number</label>
                                <span className="room-number">{booking.room_number}</span>
                            </div>
                            <div className="info-item">
                                <label>Room Type</label>
                                <span>{booking.room_type}</span>
                            </div>
                            <div className="info-item">
                                <label>Floor</label>
                                <span>{booking.floor}</span>
                            </div>
                            <div className="info-item">
                                <label>Check-in Date</label>
                                <span>{formatDate(booking.check_in_date)}</span>
                            </div>
                            <div className="info-item">
                                <label>Check-out Date</label>
                                <span>{formatDate(booking.check_out_date)}</span>
                            </div>
                            <div className="info-item">
                                <label>Booking Type</label>
                                <span>{booking.booking_type}</span>
                            </div>
                        </div>
                    </div>

                    {/* Billing Summary */}
                    <div className="card-section billing-summary">
                        <h2>Billing Summary</h2>
                        <div className="summary-table">
                            <div className="summary-row">
                                <span>Room Charges</span>
                                <span>{formatCurrency(summary?.roomCharges)}</span>
                            </div>
                            <div className="summary-row">
                                <span>Additional Charges</span>
                                <span>{formatCurrency(summary?.additionalCharges)}</span>
                            </div>
                            <div className="summary-row total">
                                <span>Grand Total</span>
                                <span>{formatCurrency(summary?.grandTotal)}</span>
                            </div>
                            <div className="summary-row paid">
                                <span>Total Paid</span>
                                <span className="text-success">- {formatCurrency(summary?.totalPaid)}</span>
                            </div>
                            {summary?.totalRefunded > 0 && (
                                <div className="summary-row refund">
                                    <span>Refunded</span>
                                    <span className="text-warning">+ {formatCurrency(summary?.totalRefunded)}</span>
                                </div>
                            )}
                            <div className="summary-row net-payable">
                                <span>Net Payable</span>
                                <span className={summary?.netPayable > 0 ? 'text-danger' : 'text-success'}>
                                    {formatCurrency(summary?.netPayable)}
                                </span>
                            </div>
                            <div className="payment-status-row">
                                <span className={`payment-badge ${getPaymentStatusBadge(summary?.paymentStatus)}`}>
                                    {summary?.paymentStatus?.toUpperCase()}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Payment History */}
                    {payments.length > 0 && (
                        <div className="card-section payments">
                            <h2>Payment History</h2>
                            <table className="data-table">
                                <thead>
                                    <tr>
                                        <th>Date</th>
                                        <th>Method</th>
                                        <th>Amount</th>
                                        <th>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {payments.map((payment, index) => (
                                        <tr key={index}>
                                            <td>{formatDate(payment.payment_date)}</td>
                                            <td>{payment.payment_method}</td>
                                            <td>{formatCurrency(payment.amount)}</td>
                                            <td>
                                                <span className={`status-small ${payment.payment_status}`}>
                                                    {payment.payment_status}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* Additional Bills */}
                    {guestBills.length > 0 && (
                        <div className="card-section guest-bills">
                            <h2>Additional Bills</h2>
                            <table className="data-table">
                                <thead>
                                    <tr>
                                        <th>Date</th>
                                        <th>Type</th>
                                        <th>Description</th>
                                        <th>Amount</th>
                                        <th>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {guestBills.map((bill, index) => (
                                        <tr key={index}>
                                            <td>{formatDate(bill.bill_date)}</td>
                                            <td className="bill-type">{bill.bill_type}</td>
                                            <td>{bill.description || '-'}</td>
                                            <td>{formatCurrency(bill.total_amount)}</td>
                                            <td>
                                                <span className={`status-small ${bill.status}`}>
                                                    {bill.status}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* Refunds */}
                    {refunds.length > 0 && (
                        <div className="card-section refunds">
                            <h2>Refund History</h2>
                            <table className="data-table">
                                <thead>
                                    <tr>
                                        <th>Date</th>
                                        <th>Type</th>
                                        <th>Amount</th>
                                        <th>Method</th>
                                        <th>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {refunds.map((refund, index) => (
                                        <tr key={index}>
                                            <td>{formatDate(refund.processed_at || refund.created_at)}</td>
                                            <td>{refund.refund_type}</td>
                                            <td>{formatCurrency(refund.refund_amount)}</td>
                                            <td>{refund.refund_method}</td>
                                            <td>
                                                <span className={`status-small ${refund.refund_status}`}>
                                                    {refund.refund_status}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* Action Buttons */}
                    <div className="card-actions">
                        <button
                            className="btn btn-primary"
                            onClick={() => navigate(`/admin/guest-bills/${bookingId}`)}
                        >
                            Add Charges
                        </button>
                        <button
                            className="btn btn-secondary"
                            onClick={() => navigate(`/admin/guest-refund/${bookingId}`)}
                        >
                            Process Refund
                        </button>
                        <button className="btn btn-outline" onClick={() => window.print()}>
                            Print Card
                        </button>
                    </div>
                </div>
            </div>
        </AdminLayout>
    );
};

export default GuestReservationCard;
